/**
 * The All lines health strip (docs/plans/oncall-line-plan.md 4 layer 4, 7.5,
 * 7.7; WP-12), for super_admins only: every site's 24-hour line with its last
 * heartbeat and active-member count, so a broken line at any site is visible
 * from one place. It reads GET /api/oncall/lines/health (403
 * ONCALL_SUPER_ADMIN_REQUIRED for anyone else) and renders nothing for a
 * viewer who is not a super_admin, so it never asks.
 *
 * Each line reads as:
 *   Problem   no active member (the call goes straight to the backups), or
 *             a heartbeat older than HEARTBEAT_OVERDUE_HOURS while the
 *             heartbeat is configured (it runs once per UTC day, plan WP-13)
 *   Check     fewer than three active members (plan 4's warning), or a
 *             configured heartbeat that has not reached the line yet
 *   OK        otherwise
 * The heartbeat's age is shown relative ("3 h ago"), so no timezone is
 * guessed for another site; the exact instant is in the title.
 *
 * The line's own number is the one printed on the ICF, so it is shown in
 * full (plan 16, WP-06); no staff number is ever here.
 */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, HeartPulse, RefreshCw, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import type { OncallLineHealth, OncallLinesHealth } from '../api/oncallApi';
import { apiErrorMessage, getLinesHealth } from '../api/oncallApi';
import { formatPhone } from '../lib/format';

/** The heartbeat runs once per UTC day; older than this, the line's number or webhook may be broken. */
export const HEARTBEAT_OVERDUE_HOURS = 26;

/** Plan 4: the Team page warns under three active members. */
export const MIN_ACTIVE_MEMBERS = 3;

export type LineTone = 'ok' | 'check' | 'problem';

export interface LineVerdict {
  tone: LineTone;
  /** The heartbeat in words: "3 h ago", "No heartbeat yet", "Heartbeat off". */
  heartbeat: string;
  /** Why the line is not OK, or null. */
  reason: string | null;
}

/** An age in words: "just now", "12 min ago", "5 h ago", "3 days ago". */
export function formatAge(iso: string | null | undefined, now: Date): string | null {
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(ms)) return null;
  const minutes = Math.max(0, Math.floor((now.getTime() - ms) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

/** How one line stands (see the header). */
export function lineVerdict(line: OncallLineHealth, heartbeatConfigured: boolean, now: Date): LineVerdict {
  const age = formatAge(line.last_heartbeat_at, now);
  const ageHours = line.last_heartbeat_at ? (now.getTime() - Date.parse(line.last_heartbeat_at)) / 3_600_000 : null;
  const heartbeat = !heartbeatConfigured ? (age ? `Last heartbeat ${age}` : 'Heartbeat off') : age ? `Heartbeat ${age}` : 'No heartbeat yet';

  if ((line.active_members ?? 0) <= 0) {
    return { tone: 'problem', heartbeat, reason: 'No active members: calls go straight to the backup phones' };
  }
  if (heartbeatConfigured && ageHours !== null && Number.isFinite(ageHours) && ageHours > HEARTBEAT_OVERDUE_HOURS) {
    return { tone: 'problem', heartbeat, reason: 'The daily heartbeat has not reached this line' };
  }
  if (line.active_members < MIN_ACTIVE_MEMBERS) {
    return { tone: 'check', heartbeat, reason: `Only ${line.active_members} active member${line.active_members === 1 ? '' : 's'}` };
  }
  if (heartbeatConfigured && !age) {
    return { tone: 'check', heartbeat, reason: 'No heartbeat has reached this line yet' };
  }
  return { tone: 'ok', heartbeat, reason: null };
}

const TONE_CLS: Record<LineTone, string> = {
  ok: 'border-gray-200 dark:border-gray-700',
  check: 'border-amber-300 dark:border-amber-700',
  problem: 'border-red-300 dark:border-red-700',
};

const TONE_ICON: Record<LineTone, { Icon: typeof CheckCircle2; cls: string; label: string }> = {
  ok: { Icon: CheckCircle2, cls: 'text-green-600 dark:text-green-400', label: 'OK' },
  check: { Icon: AlertTriangle, cls: 'text-amber-600 dark:text-amber-400', label: 'Check' },
  problem: { Icon: AlertTriangle, cls: 'text-red-600 dark:text-red-400', label: 'Problem' },
};

interface AllLinesStripProps {
  /** Bump to read the lines again (CallsTab bumps it with every poll of the list). */
  refreshKey?: number;
  /** The site in the header, highlighted. */
  currentSiteId?: string | null;
  /** "Now", for the ages; tests pass a fixed clock. */
  now?: () => Date;
}

type StripState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: OncallLinesHealth }
  | { kind: 'error'; message: string };

export default function AllLinesStrip({ refreshKey = 0, currentSiteId = null, now = () => new Date() }: AllLinesStripProps) {
  const { isSuperAdmin } = useAuth();
  const [state, setState] = useState<StripState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    const request = ++requestRef.current;
    getLinesHealth()
      .then((data) => {
        if (request === requestRef.current) setState({ kind: 'ready', data });
      })
      .catch((err) => {
        // A failed refresh keeps the last good strip; only a first failure shows the error.
        if (request === requestRef.current) {
          setState((prev) =>
            prev.kind === 'ready' ? prev : { kind: 'error', message: apiErrorMessage(err, 'The lines could not be loaded') },
          );
        }
      });
    return () => {
      requestRef.current += 1;
    };
  }, [isSuperAdmin, refreshKey, attempt]);

  if (!isSuperAdmin) return null;

  const heading = (
    <h2 className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">All lines</h2>
  );

  if (state.kind === 'loading') {
    return (
      <section aria-label="All lines" className="space-y-2" data-testid="oncall-all-lines">
        {heading}
        <div className="h-16 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section aria-label="All lines" className="space-y-2" data-testid="oncall-all-lines">
        {heading}
        <p role="alert" className="flex flex-wrap items-center gap-2 text-sm text-red-700 dark:text-red-400" data-testid="oncall-all-lines-error">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{/[.!?]$/.test(state.message) ? state.message : `${state.message}.`}</span>
          <button
            type="button"
            onClick={() => {
              setState({ kind: 'loading' });
              setAttempt((n) => n + 1);
            }}
            className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/40 sm:min-h-0"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Retry
          </button>
        </p>
      </section>
    );
  }

  const { lines, heartbeat_configured: heartbeatConfigured } = state.data;
  const at = now();
  const problems = lines.filter((l) => lineVerdict(l, heartbeatConfigured, at).tone === 'problem').length;

  return (
    <section aria-label="All lines" className="space-y-2" data-testid="oncall-all-lines">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {heading}
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {lines.length} line{lines.length === 1 ? '' : 's'}
          {problems > 0 && (
            <span className="ml-1 font-medium text-red-700 dark:text-red-400">
              · {problems} need{problems === 1 ? 's' : ''} attention
            </span>
          )}
        </span>
        {!heartbeatConfigured && (
          <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="oncall-all-lines-heartbeat-off">
            The daily heartbeat is off (ONCALL_HEARTBEAT_FROM is not set).
          </span>
        )}
      </div>
      {lines.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="oncall-all-lines-empty">
          No site has a 24-hour line yet.
        </p>
      ) : (
        <ul className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [-webkit-overflow-scrolling:touch] sm:mx-0 sm:flex-wrap sm:overflow-x-visible sm:px-0 sm:pb-0">
          {lines.map((line) => {
            const verdict = lineVerdict(line, heartbeatConfigured, at);
            const tone = TONE_ICON[verdict.tone];
            const ToneIcon = tone.Icon;
            const current = line.site_id === currentSiteId;
            return (
              <li
                key={line.id}
                aria-current={current ? 'true' : undefined}
                data-testid={`oncall-line-${line.id}`}
                data-tone={verdict.tone}
                className={`w-56 flex-shrink-0 rounded-xl border bg-white p-3 dark:bg-gray-800 sm:w-60 ${TONE_CLS[verdict.tone]} ${
                  current ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{line.site_name || line.name}</p>
                    <p className="truncate text-xs tabular-nums text-gray-500 dark:text-gray-400">{formatPhone(line.twilio_number_e164)}</p>
                  </div>
                  <span className={`inline-flex flex-shrink-0 items-center gap-1 text-xs font-medium ${tone.cls}`}>
                    <ToneIcon className="h-4 w-4" aria-hidden="true" />
                    {tone.label}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                  <span className="inline-flex items-center gap-1" title={line.last_heartbeat_at ?? undefined}>
                    <HeartPulse className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                    {verdict.heartbeat}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Users className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                    {line.active_members} active
                  </span>
                </div>
                {verdict.reason && (
                  <p className={`mt-1 text-xs ${verdict.tone === 'problem' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                    {verdict.reason}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
